import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Check, ArrowLeft, ArrowRight, MapPin } from 'lucide-react';
import { CreateClientForm, ClientLocationDto } from '../../types';
import PhoneInput from '../forms/PhoneInput';

interface ClientCreationWizardProps {
  onSubmit: (clientData: CreateClientForm, locationData: Partial<ClientLocationDto>) => void;
  isLoading?: boolean;
  error?: string;
  onCancel: () => void;
}

const clientSchema = yup.object({
  name: yup.string().required('Name is required').max(100, 'Name must be less than 100 characters'),
  phone: yup.string().max(50, 'Phone must be less than 50 characters'),
  email: yup.string().email('Please enter a valid email address').max(100, 'Email must be less than 100 characters').nullable(),
  rank: yup.mixed<number | null>()
    .nullable()
    .transform((value, originalValue) => {
      // Convert empty string, null, undefined, or NaN to null
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return null;
      }
      const numValue = Number(originalValue);
      if (isNaN(numValue)) {
        return null;
      }
      return numValue;
    })
    .test('is-valid-number', 'Rank must be a number between 0 and 100', function(value) {
      // If value is null/undefined, it's valid (optional field)
      if (value === null || value === undefined) {
        return true;
      }
      // If it's a number, validate range
      return typeof value === 'number' && value >= 0 && value <= 100;
    })
}).required();

const locationSchema = yup.object({
  locationName: yup.string().required('Location name is required').max(150, 'Location name must be less than 150 characters'),
  locationType: yup.number().required('Location type is required').min(0).max(4),
  // All other fields are optional to match backend CreateClientLocationDto
  address: yup.string().max(255, 'Address must be less than 255 characters').nullable(),
  city: yup.string().max(100, 'City must be less than 100 characters').nullable(),
  state: yup.string().max(50, 'State must be less than 50 characters').nullable(),
  zipCode: yup.string().max(20, 'Zip code must be less than 20 characters').nullable(),
  contactName: yup.string().max(150, 'Contact name must be less than 150 characters').nullable(),
  contactPhone: yup.string().max(20, 'Contact phone must be less than 20 characters').nullable(),
  contactEmail: yup.string().email('Please enter a valid email address').max(256, 'Email must be less than 256 characters').nullable(),
  specialInstructions: yup.string().max(500, 'Special instructions must be less than 500 characters').nullable()
}).required();

type ClientFormData = yup.InferType<typeof clientSchema>;
type LocationFormData = yup.InferType<typeof locationSchema>;

const ClientCreationWizard: React.FC<ClientCreationWizardProps> = ({ 
  onSubmit, 
  isLoading = false, 
  error, 
  onCancel 
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [clientData, setClientData] = useState<ClientFormData | null>(null);

  const clientForm = useForm<ClientFormData>({
    resolver: yupResolver(clientSchema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      rank: undefined
    }
  });

  const locationForm = useForm<LocationFormData>({
    resolver: yupResolver(locationSchema),
    defaultValues: {
      locationName: '',
      locationType: 1, // Store
      address: '',
      city: '',
      state: '',
      zipCode: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      specialInstructions: ''
    }
  });

  const handleClientSubmit = (data: ClientFormData) => {
    console.log('Client form submitted with data:', data);
    setClientData(data);
    setCurrentStep(2);
  };

  const handleLocationSubmit = (data: LocationFormData) => {
    if (clientData) {
      // Helper to convert empty strings to null for optional fields
      const toNullIfEmpty = (value: string | null | undefined): string | null | undefined => {
        return value && value.trim() ? value.trim() : null;
      };

      const clientFormData: CreateClientForm = {
        name: clientData.name,
        phone: toNullIfEmpty(clientData.phone) || undefined,
        email: toNullIfEmpty(clientData.email) || undefined,
        rank: clientData.rank ?? 0 // Default to 0 if null/undefined
      };

      const locationFormData: Partial<ClientLocationDto> = {
        locationName: data.locationName,
        locationType: data.locationType,
        // Convert empty strings to null for optional fields to match backend expectations
        address: toNullIfEmpty(data.address) || undefined,
        city: toNullIfEmpty(data.city) || undefined,
        state: toNullIfEmpty(data.state) || undefined,
        zipCode: toNullIfEmpty(data.zipCode) || undefined,
        contactName: toNullIfEmpty(data.contactName) || undefined,
        contactPhone: toNullIfEmpty(data.contactPhone) || undefined,
        contactEmail: toNullIfEmpty(data.contactEmail) || undefined,
        specialInstructions: toNullIfEmpty(data.specialInstructions) || undefined,
        isPrimary: true,
        isActive: true,
        canReceiveWorkOrders: true
      };

      console.log('Submitting client creation with:', { clientFormData, locationFormData });
      onSubmit(clientFormData, locationFormData);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const steps = [
    { id: 1, name: 'Client Information', description: 'Basic client details' },
    { id: 2, name: 'Primary Location', description: 'Add the first location' }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <nav aria-label="Progress">
          <ol className="flex items-center">
            {steps.map((step, stepIdx) => (
              <li key={step.name} className={`${stepIdx !== steps.length - 1 ? 'pr-8 sm:pr-20' : ''} relative`}>
                <div className="flex items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                    currentStep > step.id 
                      ? 'bg-accent-color text-white' 
                      : currentStep === step.id 
                        ? 'bg-accent-color text-white' 
                        : 'bg-gray-300 text-gray-500'
                  }`}>
                    {currentStep > step.id ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <span className="text-sm font-medium">{step.id}</span>
                    )}
                  </div>
                  <div className="ml-4 min-w-0">
                    <div className={`text-sm font-medium ${
                      currentStep >= step.id ? 'text-accent-color' : 'text-gray-500'
                    }`}>
                      {step.name}
                    </div>
                    <div className="text-sm text-gray-500">{step.description}</div>
                  </div>
                </div>
                {stepIdx !== steps.length - 1 && (
                  <div className={`absolute top-4 left-8 w-full h-0.5 ${
                    currentStep > step.id ? 'bg-accent-color' : 'bg-gray-300'
                  }`} />
                )}
              </li>
            ))}
          </ol>
        </nav>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Client Information */}
      {currentStep === 1 && (
        <form onSubmit={clientForm.handleSubmit(handleClientSubmit)} className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6">Client Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div className="md:col-span-2">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...clientForm.register('name')}
                  type="text"
                  id="name"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                  placeholder="Enter client name"
                />
                {clientForm.formState.errors.name && (
                  <p className="mt-1 text-sm text-red-600">{clientForm.formState.errors.name.message}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone
                </label>
                <PhoneInput
                  value={clientForm.watch('phone') || ''}
                  onChange={(value) => clientForm.setValue('phone', value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                  placeholder="Enter phone number"
                  id="phone"
                  name="phone"
                />
                {clientForm.formState.errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{clientForm.formState.errors.phone.message}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  {...clientForm.register('email')}
                  type="email"
                  id="email"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                  placeholder="Enter email address"
                />
                {clientForm.formState.errors.email && (
                  <p className="mt-1 text-sm text-red-600">{clientForm.formState.errors.email.message}</p>
                )}
              </div>

              {/* Rank */}
              <div>
                <label htmlFor="rank" className="block text-sm font-medium text-gray-700">
                  Rank
                </label>
                <input
                  {...clientForm.register('rank', { 
                    setValueAs: (value) => {
                      // Handle empty string, null, undefined, or NaN - return empty string to let yup transform handle it
                      if (value === '' || value === null || value === undefined) {
                        return '';
                      }
                      const numValue = Number(value);
                      if (isNaN(numValue)) {
                        return '';
                      }
                      return numValue;
                    }
                  })}
                  type="number"
                  id="rank"
                  min="0"
                  max="100"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                  placeholder="Enter rank (0-100, optional)"
                />
                {clientForm.formState.errors.rank && (
                  <p className="mt-1 text-sm text-red-600">{clientForm.formState.errors.rank.message}</p>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-accent-color border border-transparent rounded-md hover:bg-accent-color-600 flex items-center"
            >
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
          </div>
        </form>
      )}

      {/* Step 2: Primary Location */}
      {currentStep === 2 && (
        <form onSubmit={locationForm.handleSubmit(handleLocationSubmit)} className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center mb-6">
              <MapPin className="h-6 w-6 text-accent-color mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Primary Location</h3>
            </div>
            
            <div className="mb-4 p-4 bg-accent-color-50 rounded-md">
              <p className="text-sm text-accent-color-800">
                This will be the primary location for <strong>{clientData?.name}</strong>. 
                You can add more locations after creating the client.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Location Name */}
              <div className="md:col-span-2">
                <label htmlFor="locationName" className="block text-sm font-medium text-gray-700">
                  Location Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...locationForm.register('locationName')}
                  type="text"
                  id="locationName"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                  placeholder="e.g., Main Store, Headquarters, etc."
                />
                {locationForm.formState.errors.locationName && (
                  <p className="mt-1 text-sm text-red-600">{locationForm.formState.errors.locationName.message}</p>
                )}
              </div>

              {/* Location Type */}
              <div>
                <label htmlFor="locationType" className="block text-sm font-medium text-gray-700">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  {...locationForm.register('locationType')}
                  id="locationType"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                >
                  <option value={0}>Headquarters</option>
                  <option value={1}>Store</option>
                  <option value={2}>Warehouse</option>
                  <option value={3}>Distribution</option>
                  <option value={4}>Other</option>
                </select>
                {locationForm.formState.errors.locationType && (
                  <p className="mt-1 text-sm text-red-600">{locationForm.formState.errors.locationType.message}</p>
                )}
              </div>

              {/* Contact Name */}
              <div>
                <label htmlFor="contactName" className="block text-sm font-medium text-gray-700">
                  Contact Name
                </label>
                <input
                  {...locationForm.register('contactName')}
                  type="text"
                  id="contactName"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                  placeholder="Enter contact name"
                />
                {locationForm.formState.errors.contactName && (
                  <p className="mt-1 text-sm text-red-600">{locationForm.formState.errors.contactName.message}</p>
                )}
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                  Address
                </label>
                <input
                  {...locationForm.register('address')}
                  type="text"
                  id="address"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                  placeholder="Enter street address"
                />
                {locationForm.formState.errors.address && (
                  <p className="mt-1 text-sm text-red-600">{locationForm.formState.errors.address.message}</p>
                )}
              </div>

              {/* City */}
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                  City
                </label>
                <input
                  {...locationForm.register('city')}
                  type="text"
                  id="city"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                  placeholder="Enter city"
                />
                {locationForm.formState.errors.city && (
                  <p className="mt-1 text-sm text-red-600">{locationForm.formState.errors.city.message}</p>
                )}
              </div>

              {/* State */}
              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                  State
                </label>
                <select
                  {...locationForm.register('state')}
                  id="state"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                >
                  <option value="">Select State</option>
                  <option value="AL">Alabama (AL)</option>
                  <option value="AK">Alaska (AK)</option>
                  <option value="AZ">Arizona (AZ)</option>
                  <option value="AR">Arkansas (AR)</option>
                  <option value="CA">California (CA)</option>
                  <option value="CO">Colorado (CO)</option>
                  <option value="CT">Connecticut (CT)</option>
                  <option value="DE">Delaware (DE)</option>
                  <option value="FL">Florida (FL)</option>
                  <option value="GA">Georgia (GA)</option>
                  <option value="HI">Hawaii (HI)</option>
                  <option value="ID">Idaho (ID)</option>
                  <option value="IL">Illinois (IL)</option>
                  <option value="IN">Indiana (IN)</option>
                  <option value="IA">Iowa (IA)</option>
                  <option value="KS">Kansas (KS)</option>
                  <option value="KY">Kentucky (KY)</option>
                  <option value="LA">Louisiana (LA)</option>
                  <option value="ME">Maine (ME)</option>
                  <option value="MD">Maryland (MD)</option>
                  <option value="MA">Massachusetts (MA)</option>
                  <option value="MI">Michigan (MI)</option>
                  <option value="MN">Minnesota (MN)</option>
                  <option value="MS">Mississippi (MS)</option>
                  <option value="MO">Missouri (MO)</option>
                  <option value="MT">Montana (MT)</option>
                  <option value="NE">Nebraska (NE)</option>
                  <option value="NV">Nevada (NV)</option>
                  <option value="NH">New Hampshire (NH)</option>
                  <option value="NJ">New Jersey (NJ)</option>
                  <option value="NM">New Mexico (NM)</option>
                  <option value="NY">New York (NY)</option>
                  <option value="NC">North Carolina (NC)</option>
                  <option value="ND">North Dakota (ND)</option>
                  <option value="OH">Ohio (OH)</option>
                  <option value="OK">Oklahoma (OK)</option>
                  <option value="OR">Oregon (OR)</option>
                  <option value="PA">Pennsylvania (PA)</option>
                  <option value="RI">Rhode Island (RI)</option>
                  <option value="SC">South Carolina (SC)</option>
                  <option value="SD">South Dakota (SD)</option>
                  <option value="TN">Tennessee (TN)</option>
                  <option value="TX">Texas (TX)</option>
                  <option value="UT">Utah (UT)</option>
                  <option value="VT">Vermont (VT)</option>
                  <option value="VA">Virginia (VA)</option>
                  <option value="WA">Washington (WA)</option>
                  <option value="WV">West Virginia (WV)</option>
                  <option value="WI">Wisconsin (WI)</option>
                  <option value="WY">Wyoming (WY)</option>
                  <option value="DC">District of Columbia (DC)</option>
                </select>
                {locationForm.formState.errors.state && (
                  <p className="mt-1 text-sm text-red-600">{locationForm.formState.errors.state.message}</p>
                )}
              </div>

              {/* Zip Code */}
              <div>
                <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">
                  Zip Code
                </label>
                <input
                  {...locationForm.register('zipCode')}
                  type="text"
                  id="zipCode"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                  placeholder="Enter zip code"
                />
                {locationForm.formState.errors.zipCode && (
                  <p className="mt-1 text-sm text-red-600">{locationForm.formState.errors.zipCode.message}</p>
                )}
              </div>

              {/* Contact Phone */}
              <div>
                <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700">
                  Contact Phone
                </label>
                <PhoneInput
                  value={locationForm.watch('contactPhone') || ''}
                  onChange={(value) => locationForm.setValue('contactPhone', value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                  placeholder="Enter contact phone"
                  id="contactPhone"
                  name="contactPhone"
                />
                {locationForm.formState.errors.contactPhone && (
                  <p className="mt-1 text-sm text-red-600">{locationForm.formState.errors.contactPhone.message}</p>
                )}
              </div>

              {/* Contact Email */}
              <div>
                <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700">
                  Contact Email
                </label>
                <input
                  {...locationForm.register('contactEmail')}
                  type="email"
                  id="contactEmail"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                  placeholder="Enter contact email"
                />
                {locationForm.formState.errors.contactEmail && (
                  <p className="mt-1 text-sm text-red-600">{locationForm.formState.errors.contactEmail.message}</p>
                )}
              </div>

              {/* Special Instructions */}
              <div className="md:col-span-2">
                <label htmlFor="specialInstructions" className="block text-sm font-medium text-gray-700">
                  Special Instructions
                </label>
                <textarea
                  {...locationForm.register('specialInstructions')}
                  id="specialInstructions"
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                  placeholder="Enter any special instructions for this location"
                />
                {locationForm.formState.errors.specialInstructions && (
                  <p className="mt-1 text-sm text-red-600">{locationForm.formState.errors.specialInstructions.message}</p>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              onClick={handleBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </button>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-accent-color border border-transparent rounded-md hover:bg-accent-color-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    Create Client
                    <Check className="ml-2 h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default ClientCreationWizard;
